import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import verticalVideoMin from 'assets/creative/vertical-video-min.png';
import verticalVideoMax from 'assets/creative/vertical-video-max.png';
import horizontalVideoMin from 'assets/creative/horizontal-video-min.png';
import horizontalVideoMax from 'assets/creative/horizontal-video-max.png';
import verticalImageMin from 'assets/creative/vertical-image-min.png';
import verticalImageMax from 'assets/creative/vertical-image-max.png';
import horizontalImageMin from 'assets/creative/horizontal-image-min.png';
import horizontalImageMax from 'assets/creative/horizontal-image-max.png';
import i18n from 'i18n';
import styles from './outdoorCreativeSizeHint.module.scss';
import { ImagePreview } from 'components/common/Image/ImagePreview';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

export const OutdoorCreativeSizeHint: React.FC<{
  isVideo: boolean;
  sizeBoxingMap: {
    [key: string]: { width: [number, number]; height: [number, number] };
  };
  creativeSize: string;
  closeHint: () => void;
}> = ({ isVideo, creativeSize, sizeBoxingMap, closeHint }) => {
  const verticalMin = isVideo ? verticalVideoMin : verticalImageMin;
  const verticalMax = isVideo ? verticalVideoMax : verticalImageMax;
  const horizontalMin = isVideo ? horizontalVideoMin : horizontalImageMin;
  const horizontalMax = isVideo ? horizontalVideoMax : horizontalImageMax;
  const renderExample = useCallback(() => {
    const widthHeight = creativeSize.split(' x ');
    const horizontal =
      parseInt(widthHeight[0], 10) >= parseInt(widthHeight[1], 10);
    const boxingKey = `${widthHeight[0]}x${widthHeight[1]}`;
    if (!sizeBoxingMap[boxingKey]) {
      return null;
    }
    const { width, height } = sizeBoxingMap[boxingKey];
    const minSizeTitle = horizontal
      ? i18n.t('outdoorCreativeSizeHint.labels.minSize') +
        `${width[0]} x ${height[0]}`
      : i18n.t('outdoorCreativeSizeHint.labels.minSize');
    const maxSizeTitle = horizontal
      ? i18n.t('outdoorCreativeSizeHint.labels.maxSize') +
        `${width[1]} x ${height[1]}`
      : i18n.t('outdoorCreativeSizeHint.labels.maxSize');
    const minSizeFooter = horizontal ? null : `${width[0]} x ${height[0]}`;
    const maxSizeFooter = horizontal ? null : `${width[1]} x ${height[1]}`;
    return (
      <>
        <div className={styles.example}>
          <div>{minSizeTitle}</div>
          <div className={styles.imagePreview}>
            <ImagePreview
              size={{ width: width[0] / 5, height: height[0] / 5 }}
              border={false}
              url={horizontal ? horizontalMin : verticalMin}
              config={{
                showName: false,
                showSize: false
              }}
            />
          </div>
          <div>{minSizeFooter}</div>
        </div>
        <div className={styles.example}>
          <div>{maxSizeTitle}</div>
          <div className={styles.imagePreview}>
            <ImagePreview
              size={{ width: width[1] / 5, height: height[1] / 5 }}
              border={false}
              url={horizontal ? horizontalMax : verticalMax}
              config={{
                showName: false,
                showSize: false
              }}
            />
          </div>
          <div>{maxSizeFooter}</div>
        </div>
      </>
    );
  }, [
    sizeBoxingMap,
    creativeSize,
    horizontalMin,
    horizontalMax,
    verticalMin,
    verticalMax
  ]);

  if (!creativeSize) {
    return null;
  }

  const widthHeight = creativeSize.split(' x ');
  const width = parseInt(widthHeight[0], 10);
  const height = parseInt(widthHeight[1], 10);
  const horizontal = width >= height;
  const sizeRangeExampleClass = cx(styles.sizeRangeExample, {
    horizontal: horizontal,
    vertical: !horizontal
  });

  return (
    <div className={styles.outdoorCreativeSizeHint}>
      <div className={styles.title}>
        <span>{i18n.t('outdoorCreativeSizeHint.labels.creativeInfoHint')}</span>
        <FontAwesomeIcon icon={faTimes} onClick={closeHint} />
      </div>
      <div className={styles.sizeRange}>
        <div className={styles.subTitle}>
          <span>
            {i18n.t('outdoorCreativeSizeHint.labels.sizeInstruction')}
          </span>
        </div>
        <div className={sizeRangeExampleClass}>{renderExample()}</div>
      </div>
    </div>
  );
};
